// Centralized SEO copy. Each entry: title (50–60 chars) + description (150–160).
// Unified location anchor: "Scottsdale, Phoenix & New York."
//
// Phase 16 — OG / Twitter sweep:
// Every entry now carries an ogImage (per-page hero where identifiable; the
// polished clinic portrait as the universal fallback). The Seo helper emits
// twitter:card = summary_large_image by default. Per-page Cloudinary URLs
// are pinned at OG's recommended 1200×630 with c_fill + ar_1.91:1 (Facebook's
// preferred ratio, accepted by Twitter/LinkedIn) so the social card renders
// without unexpected crops. Override per page by editing the publicId below.

const SITE = "Elizaveta von Stuben";
const SITE_URL = "https://vonstuben.com";
const CLOUDINARY = "https://res.cloudinary.com/dub8rhyez/image/upload";

// Universal fallback OG asset: the polished provider portrait that already
// served Phase 12 clinic. Used for pages without an identifiable hero
// publicId. 1200×630 with c_fill keeps composition centered.
const OG_FALLBACK = `${CLOUDINARY}/f_auto,q_auto,w_1200,h_630,c_fill/01_Corporate_24_nzlgwo.jpg`;

// Helper: build an OG image URL from a Cloudinary publicId. Pinned to the
// social-card canonical 1200×630 dimensions so the same asset can be used
// for og:image AND twitter:image without per-platform variants.
const og = (publicId) =>
    publicId
        ? `${CLOUDINARY}/f_auto,q_auto,w_1200,h_630,c_fill/${publicId}`
        : OG_FALLBACK;

export const SEO = {
    home: {
        title: `${SITE} — Editorial Photography · Scottsdale, Phoenix & New York`,
        description:
            "Editorial portrait, family, and brand photography by Elizaveta von Stuben — refined sessions for life moments and growing businesses across Scottsdale, Phoenix & New York.",
        canonical: `${SITE_URL}/`,
        ogImage: og("01_Corporate_24_nzlgwo"),
        robots: "index, follow",
    },
    lifeLegacy: {
        title: `Life & Legacy Studio — Portraits, Family & Story Sessions | ${SITE}`,
        description:
            "Life & Legacy Studio — editorial portraits, family, motherhood, and milestone photography in Scottsdale, Phoenix & New York. Calm, guided sessions designed to be remembered.",
        canonical: `${SITE_URL}/life-legacy`,
        ogImage: og("_portfolio_Taylor_4_39_sm_gmpecx"),
        robots: "index, follow",
    },
    business: {
        title: `Arizona Brand & Event Photography | ${SITE}`,
        description:
            "Professional photography and video for Scottsdale and Phoenix hospitality, medical, events, and growing businesses. Summer special pricing available.",
        canonical: `${SITE_URL}/business`,
        ogImage: og("01_Corporate_24_nzlgwo"),
        robots: "index, follow",
    },
    brandBusiness: {
        title: `Brand & Business Studio — Editorial Brand Photography | ${SITE}`,
        description:
            "Editorial brand and business photography — provider portraits, founder portraits, and polished content for clinics, restaurants, hospitality, and growing brands.",
        canonical: `${SITE_URL}/brand-business`,
        ogImage: og("02_Corporate_19_sgdfng"),
        robots: "index, follow",
    },
    portfolio: {
        title: `Portfolio — Selected Work | ${SITE}`,
        description:
            "A curated selection of recent work — portraits, family stories, branding, business content, and editorial commissions across Scottsdale, Phoenix & New York.",
        canonical: `${SITE_URL}/portfolio`,
        ogImage: og("03_Corporate_13_o16cwq"),
        robots: "index, follow",
    },
    atelier: {
        title: `The Atelier — Fine Art Prints | ${SITE}`,
        description:
            "Limited-edition archival fine art prints from the Elizaveta von Stuben atelier — editorial portraits, travel, and signature series, archival paper and museum-grade framing.",
        canonical: `${SITE_URL}/atelier`,
        ogImage: og("04_Corporate_28_xx7y2a"),
        robots: "index, follow",
    },
    mentoring: {
        title: `Mentoring & Workshops — One-on-One Photography Mentoring | ${SITE}`,
        description:
            "One-on-one mentoring and small-group workshops with Elizaveta von Stuben — refined editorial portraiture, light, and business of photography for emerging photographers.",
        canonical: `${SITE_URL}/mentoring`,
        ogImage: og("05_Corporate_10_jsr2ba"),
        robots: "index, follow",
    },
    reviews: {
        title: `Reviews — Client Stories | ${SITE}`,
        description:
            "What clients say about working with Elizaveta von Stuben — calm, professional, on-brand photography for families, brands, and editorial commissions.",
        canonical: `${SITE_URL}/reviews`,
        ogImage: og("06_Corporate_25_ryvvp9"),
        robots: "index, follow",
    },
    inquire: {
        title: `Inquire — Reserve a Session | ${SITE}`,
        description:
            "Tell me what you'd like photographed — sessions, story sessions, brand content, and custom commissions across Scottsdale, Phoenix & New York.",
        canonical: `${SITE_URL}/inquire`,
        ogImage: OG_FALLBACK,
        robots: "index, follow",
    },
    journal: {
        title: `Journal — Notes from the Studio | ${SITE}`,
        description:
            "Notes from the studio — essays on portraiture, light, brand visuals, and the craft behind a calm, editorial photography practice.",
        canonical: `${SITE_URL}/journal`,
        ogImage: og("3036-316_Corporate_23_tftw0g"),
        robots: "index, follow",
    },
    journalPost: {
        // Per-post values overridden by JournalPost.jsx at render time (it
        // injects post.title/description/heroImage). This default just
        // covers a direct-link fallback if the post page renders before
        // its data loads.
        title: `Journal — ${SITE}`,
        description:
            "Notes from the Elizaveta von Stuben studio — editorial photography essays, light, and craft.",
        canonical: `${SITE_URL}/journal`,
        ogImage: og("3036-316_Corporate_23_tftw0g"),
        robots: "index, follow",
    },
    prints: {
        title: `Fine Art Prints — Atelier | ${SITE}`,
        description:
            "Limited-edition archival fine art prints — editorial portraits, travel, and signature series. Museum-grade paper, hand-numbered and signed.",
        canonical: `${SITE_URL}/atelier`,
        ogImage: og("04_Corporate_28_xx7y2a"),
        robots: "index, follow",
    },
    printDetail: {
        // Per-print values overridden by PrintDetail.jsx at render with
        // the live print title + edition image. This is the safe default.
        title: `Fine Art Print — Atelier | ${SITE}`,
        description:
            "Limited-edition archival fine art print from the Elizaveta von Stuben atelier — museum-grade paper, hand-numbered and signed.",
        canonical: `${SITE_URL}/atelier`,
        ogImage: og("04_Corporate_28_xx7y2a"),
        robots: "index, follow",
    },
    workshops: {
        title: `Workshops & Mentoring — ${SITE}`,
        description:
            "Small-group workshops and one-on-one mentoring with Elizaveta von Stuben — refined editorial portraiture, light, posing, and the business of photography.",
        canonical: `${SITE_URL}/mentoring`,
        ogImage: og("05_Corporate_10_jsr2ba"),
        robots: "index, follow",
    },
    about: {
        title: `About — ${SITE}`,
        description:
            "About Elizaveta von Stuben — editorial photographer based in Scottsdale, working with families, brands, and editorial clients across Phoenix & New York.",
        canonical: `${SITE_URL}/about`,
        ogImage: og("02_Corporate_19_sgdfng"),
        robots: "index, follow",
    },
    // Phase 12/13/13B — Aesthetic / wellness / dental campaign landing page.
    // Unlisted in top nav (campaign-driven traffic only) but crawlable.
    aestheticClinic: {
        title: `Aesthetic, Wellness & Dental Practice Photography — ${SITE} Imagery`,
        description:
            "Photo and video for aesthetic, wellness, and dental practices in Scottsdale & Phoenix — provider portraits, polished space details, and short content for website, Google, booking pages, and social.",
        canonical: `${SITE_URL}/aesthetic-clinic-photography`,
        // OG image = the actual med-spa hero visible on the page (TheParkMed
        // shoot) so the social-share preview matches what a first-time
        // visitor sees at the top of the landing page. Was previously the
        // generic corporate portrait 01_Corporate_24_nzlgwo, which felt
        // off-brand for a clinic-audience share.
        ogImage: og("TheParkMed_11082022_-_44_mxigkq"),
        robots: "index, follow",
    },
    // Home-Services campaign landing page (HVAC / plumbing / electrical /
    // roofing / home services in Arizona). Unlisted in top nav, crawlable.
    // OG image: existing "team/partnership" business asset
    // (02_Corporate_19_sgdfng) reads as the most "established team / real
    // operation" of the existing Cloudinary assets — flag for Eli to swap
    // to a real on-the-job or fleet/team asset when she has one shot.
    homeServices: {
        title: `Home-Service Photography & Video — HVAC, Plumbing & Trades in Arizona | ${SITE} Imagery`,
        description:
            "Photography and video for HVAC, plumbing, electrical, and home-service companies in Phoenix, Scottsdale, and across Arizona — team, fleet, and finished-work content for your website, Google profile, ads, and social. Photo Day from $1,050.",
        canonical: `${SITE_URL}/home-services-photography`,
        ogImage: og("02_Corporate_19_sgdfng"),
        robots: "index, follow",
    },
};
